import { InfoIcon } from "lucide-react";
import React from "react";
import { Grid, GridRow, Popup } from "semantic-ui-react";
import {
  InputText,
  InputTextArea,
  NewImageUploader,
  Switch,
  TitleView
} from "../../../../components";
import { DISABLE_IS_ROOT_PRODUCT_BUTTON } from "../../../../config/constants";
import { IsRootProduct } from "../../../../config/enums/ProductType.enum";
import { useConvertToAverageEligibility } from "../../../../hooks/useConvertToAverageEligibility";
import { validateCharacterLength } from "../../../../utils/utils";
import { ProductInfoFormProps } from "../types/productTypes";

const ProductInfoForm: React.FC<ProductInfoFormProps> = ({
  register,
  errors,
  productDetails,
  setProductType,
  isRootProduct,
  handleAverageProduct,
  uploadProductImage,
  deleteProductImage,
  refetch
}) => {
  // Check conversion eligibility
  const { isEligible, reason, isLoading } = useConvertToAverageEligibility(productDetails?._id);

  // Get current state of the toggle
  const currentIsAverage = isRootProduct === IsRootProduct.YES;

  // Disable logic:
  // - If currently YES: allow switching to NO (not disabled)
  // - If currently NO: only allow switching to YES if eligible
  const isConversionDisabled = !currentIsAverage && !isEligible;

  const titleWithPopup = [
    <Grid>
      <GridRow textAlign="center" className="pl-1 customButtonAverageSwitch">
        <span>Average Impact Product</span>
        <Popup
          content={`Generates an ''average product'' using the carbon footprint and volume of selected products`}
          trigger={<InfoIcon className="ml-1" size={20} color="green" />}
        />
      </GridRow>
    </Grid>
  ];
  return (
    <>
      <TitleView title="Product Info" CustomTitleViewMain="productEditTitle" />
      <Grid>
        <Grid.Column width={16}>
          <Grid>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <InputText
                register={register}
                errors={errors.product_name}
                labelName={"Name"}
                placeholder="Name"
                name="product_name"
                required={true}
                errorMessage={"Please enter ingredient name"}
                validateHandle={(value: any) => {
                  validateCharacterLength(
                    value,
                    32,
                    "Ingredient name cannot exceed 32 characters"
                  );
                }}
              />
            </Grid.Column>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <InputText
                register={register}
                errors={errors.registration_number}
                labelName={"Register Number"}
                placeholder="Register Number"
                name="registration_number"
              />
            </Grid.Column>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <Switch
                label={"Product Type"}
                onClick={(val: any) => setProductType(val)}
                titleOne={"Processed Product"}
                titleTwo={"Farm Product"}
                defaultValue={productDetails?.product_type}
                titleOneValue={"PROCESS"}
                titleTwoValue={"FARM"}
              />
            </Grid.Column>
            {!DISABLE_IS_ROOT_PRODUCT_BUTTON && (
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <Popup
                  content={reason}
                  disabled={!isConversionDisabled || isLoading}
                  trigger={
                    <div>
                      <Switch
                        customTitleStatus
                        customTitle={titleWithPopup}
                        onClick={(val: any) => {
                          if (!isConversionDisabled) {
                            handleAverageProduct(val);
                          }
                        }}
                        titleOne={IsRootProduct.YES}
                        titleTwo={IsRootProduct.NO}
                        defaultValue={isRootProduct}
                        titleOneValue={IsRootProduct.YES}
                        titleTwoValue={IsRootProduct.NO}
                        disabled={isConversionDisabled}
                      />
                    </div>
                  }
                  position="top center"
                  size="small"
                  inverted
                />
              </Grid.Column>
            )}

            <Grid.Column computer={8} tablet={16} mobile={16}>
              <NewImageUploader
                defaultImage={productDetails?.product_image_path}
                buttonTitle={"Upload an Image"}
                imageData={(file: any) => {
                  if (file) {
                    uploadProductImage(
                      { product_id: productDetails?._id, file },
                      { onSuccess: () => refetch(productDetails?._id) }
                    );
                  }
                }}
                deleteImage={() => {
                  deleteProductImage(
                    { product_id: productDetails?._id },
                    { onSuccess: () => refetch(productDetails?._id) }
                  );
                }}
                customImageDeleteMain={"customProductImageViewMain"}
              />
            </Grid.Column>

            <Grid.Column width={16} className="paddingRemoveTop mb-1">
              <InputTextArea
                register={register}
                errors={errors.product_other_info}
                labelName={"Additional Information"}
                placeholder="Additional Information"
                name="product_other_info"
                customInputValidation
              />
            </Grid.Column>
          </Grid>
        </Grid.Column>
      </Grid>
    </>
  );
};

export default ProductInfoForm;
